import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ModalBody } from 'reactstrap';
import {
  VrrButton,
  VrrCircleLoader,
  VrrModal,
  VrrTable
} from '../../../shared_components';
import LabelModal from '../label_modal';
import { PrivateComponent } from '../../../permissions';
import { getFeatureByPath } from '../../../routing/services';
import { prepareBody, prepareHeader } from './services';
import useSettings from '../../../../libs/settings/useSettings';
import { getAccounts, getFeatures } from '../../../../redux/app/appActions';
import { getVrrPreferences } from '../../../../redux/settings/settingAction';
import {
  fetchConnectedAccountCallback,
  fetchConnectedAccountURL,
  getConnectedAccountURL,
  isConnectedToAccount
} from '../../../../redux/userProfile/userProfileActions';
import { TAccountsAPI } from '../types';
import { TAccountModal } from './types';
import './AccountModal.scss';

const AccountModal: React.FC<TAccountModal> = ({
  isOpen,
  onCancelAction,
  onSubmitAction
}) => {
  const { t } = useTranslation();
  const { vrr } = useSettings();
  const dispatch: any = useDispatch();
  const features = useSelector(getFeatures);
  const accounts = useSelector(getAccounts);
  const accountUrl = useSelector(getConnectedAccountURL);
  const isConnected = useSelector(isConnectedToAccount);
  const currentBrand = useSelector(getVrrPreferences)?.current_brand_id;
  const [newAccounts, setNewAccounts] = useState<TAccountsAPI[]>([]);
  const [selectedAccount, setSelectedAccount] = React.useState('');

  useEffect(() => {
    const codeByGoogle = {
      code: new URL(window.location.href).searchParams.get('code')
    };

    if (codeByGoogle.code === null) {
      dispatch(fetchConnectedAccountURL());
    } else {
      dispatch(fetchConnectedAccountCallback(codeByGoogle));
    }
  }, []);

  useEffect(() => {
    return () => {
      setNewAccounts([]);
    };
  }, []);

  useEffect(() => {
    if (accounts) {
      setNewAccounts([...accounts]);
    }
  }, [accounts]);

  const onClickPlusButton = (account: string) => {
    setSelectedAccount(account);
  };

  const onCancel = () => {
    if (onCancelAction) onCancelAction();
  };

  const onSubmit = () => {
    if (onSubmitAction) onSubmitAction(newAccounts);
    onCancel();
  };

  const onChange = (value: string, isChecked: boolean) => {
    let dataTable = [...newAccounts];
    dataTable = dataTable.map((account) =>
      account.vrr_id.toString() === value
        ? {
            ...account,
            role: isChecked ? 'media_buyer' : 'available'
          }
        : account
    );
    setNewAccounts(dataTable);
  };

  const onChangeAll = (isChecked: boolean) => {
    let dataTable = [...newAccounts];
    dataTable = dataTable.map((account) => ({
      ...account,
      role: isChecked ? 'media_buyer' : 'available'
    }));
    setNewAccounts(dataTable);
  };

  const closeModal = () => {
    setSelectedAccount('');
  };

  return (
    <>
      <VrrModal
        className="available-accounts-modal"
        isOpen={isOpen}
        title={t('connected_account.account_modal.title')}
        modalBody={
          <ModalBody className="available-accounts-modal-body">
            <VrrCircleLoader isEnable={!accounts}>
              {accounts?.length === 0 ||
              (!isConnected && typeof accountUrl === 'string') ? (
                <>
                  <VrrButton
                    className="brand-dropdown-add-brand btn-with-icon-and-label icon-left"
                    icon={{
                      iconLabel: 'tim-icons icon-cloud-download-93'
                    }}
                    onClick={() =>
                      (window.location.href = accountUrl as string)
                    }
                  >
                    {t('components.connected_account.button.connect_account')}
                  </VrrButton>
                  <p>
                    {t(
                      'connected_account.account_modal.body.no_available_accounts'
                    )}
                  </p>
                </>
              ) : (
                <VrrTable
                  header={prepareHeader(onChangeAll)}
                  body={prepareBody(newAccounts, onChange)}
                  tableProps={{
                    borderless: true,
                    striped: true,
                    className: 'accounts-table'
                  }}
                  actions={[
                    {
                      id: 'addLabel',
                      component: (rowId: string) => (
                        <PrivateComponent
                          authorizationStrategy={() =>
                            getFeatureByPath(features, 'navbar.add_labels')
                          }
                        >
                          <VrrButton
                            className="btn-link add-label"
                            data-cy-id="user-profile-add-label-button"
                            icon={{
                              iconLabel: 'tim-icons icon-simple-add'
                            }}
                            data-row={rowId}
                            data-id="addLabel"
                            onClick={(e) => {
                              if (accounts && e.currentTarget.dataset.row)
                                onClickPlusButton(
                                  accounts[
                                    parseInt(e.currentTarget.dataset.row)
                                  ].vrr_id.toString()
                                );
                            }}
                          />
                        </PrivateComponent>
                      )
                    }
                  ]}
                />
              )}
            </VrrCircleLoader>
          </ModalBody>
        }
        toggleModal={onCancel}
        toggleValidation={onSubmit}
        scrollable
        dataCyCancelButton="user-profile-add-accounts-cancel"
      />
      {selectedAccount.length > 0 && (
        <LabelModal
          id="connected-account-labels"
          brand={currentBrand}
          onSubmitAction={(result) => vrr({ custom_labels: result }, true)}
          isOpen={selectedAccount.length > 0}
          onCancelAction={closeModal}
        />
      )}
    </>
  );
};

export default AccountModal;
